import { Component, ElementRef, OnDestroy, OnInit, ViewChild, Output, EventEmitter } from '@angular/core';
import { ApolusProxyService, ApolusTypes } from 'src/app/customer-modules/apolus/shared/services/apolus-proxy.service';
import { AppService, Modal } from 'common-cloudstudio';
import { ApolusService } from 'src/app/customer-modules/apolus/shared/services/apolus.service';
import { GearAppService } from 'common-gear';
import { ToasterService } from 'angular2-toaster';

@Component({
  selector: 'app-modal-batch-devices',
  templateUrl: './modal-batch-devices.component.html',
  styleUrls: ['./modal-batch-devices.component.scss'],
})
export class ModalBatchDevicesComponent implements OnInit, OnDestroy {
  @ViewChild("modalBatchDevicesUpload") public _modalBatchDevicesUpload: Modal;
  @ViewChild("fileInput") private _fileInput: ElementRef;

  currentStep: number = 1;
  _displayUploadModal: boolean = true;
  _enableNextButton: boolean = false;
  _errorMessage: string = '';
  fileSelected: File | null = null;
  fileName: string = '';
  isLoading: boolean = false;
  tableData: any[][] = [];
  paginatedTableData: any[][] = [];
  tableHeaders: { title: string, key: string }[] = [];
  currentPage: number = 1;
  itemsPerPage: number = 10;
  totalPages: number = 1;
  validationErrors: { row: number, col: number, type: string, message: string }[] = [];
  showValidationErrors: boolean = false;
  activeTab: number = 1;
  loadedDeviceIds: any[] = [];
  @Output() devicesLoaded = new EventEmitter<number[]>();

  constructor(
    public _appService: AppService,
    public gearAppService: GearAppService,
    private _apolusProxyService: ApolusProxyService,
    public _apolusService: ApolusService,
    private _toasterService: ToasterService,
  ) {}

  ngOnInit(): void {
  }
  
  ngOnDestroy() {
  }

  openModal(tabIndex: number) {
    this.activeTab = tabIndex;
    this.resetModalState();
    this._modalBatchDevicesUpload.open();
  }

  resetModalState() {
    this.currentStep = 1;
    this._displayUploadModal = true;
    this._enableNextButton = false;
    this._errorMessage = '';
    this.fileSelected = null;
    this.fileName = '';
    this.isLoading = false;
    this.tableData = [];
    this.paginatedTableData = [];
    this.currentPage = 1;
    this.totalPages = 1;
    this.validationErrors = [];
    this.showValidationErrors = false;
    this.loadedDeviceIds = [];
  }

  onModalClose() {
    this._modalBatchDevicesUpload.close();
    this.resetModalState();
  }

  getTabName(): string {
    switch (this.activeTab) {
      case 1: return 'Válvulas';
      case 2: return 'Alarmas';
      case 3: return 'Dispositivos';
      default: return 'Dispositivos';
    }
  }

  getCSVHeaders(): string[] {
    switch (this.activeTab) {
      case 1: // Válvulas
        return ['DeviceID', 'ClientID', 'Contrato'];
      case 2: // Alarmas
        return ['DeviceID', 'ClientID', 'Contrato'];
      case 3: // Dispositivos
        return ['DeviceID', 'ClientID', 'Contrato'];
      default:
        return ['DeviceID', 'ClientID', 'Contrato'];
    }
  }

  downloadSampleFile(): void {
    const headers = this.getCSVHeaders();
    const sampleData = [
      ['1', 'CLI001', 'CTO001'],
      ['2', 'CLI002', 'CTO002'],
      ['3', 'CLI003', 'CTO003'],
    ];

    const csvContent = [
      headers,
      ...sampleData
    ];

    const csvString = csvContent.map(e => e.join(",")).join("\n");
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvString], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const date = new Date().toLocaleString();
    link.download = `dispositivos-${this.getTabName().toLowerCase()}-ejemplo-${date}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.isLoading = true;
      setTimeout(() => {
        this.fileSelected = input.files![0];
        this.fileName = this.fileSelected.name;
        this._enableNextButton = true;
        this._errorMessage = '';
        this.isLoading = false;
      }, 100);
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer?.files.length) {
      this.fileSelected = event.dataTransfer.files[0];
      this.fileName = this.fileSelected.name;
      this._enableNextButton = true;
      this._errorMessage = '';
    }
  }

  clearFile(): void { 
    this._errorMessage = '';
    this.fileSelected = null;
    this.fileName = '';
    this._enableNextButton = false;
    this.currentPage = 1;
    this.totalPages = 1;
    this.paginatedTableData = [];
  }

  proceedToNextStep() {
    if (this.fileSelected) {
      this.currentPage = 1;
      this.processCSVFile();
      if (this.validationErrors.length > 0) {
        this.showValidationErrors = true;
        this._errorMessage = `Se encontraron ${this.validationErrors.length} errores de validación. Por favor, corrija los valores marcados en rojo.`;
      }
    } 
  }

  processCSVFile() { 
    const headers = this.getCSVHeaders();
    this.tableHeaders = headers.map((header, index) => ({
      title: header,
      key: header
    }));
  
    this._errorMessage = '';
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const csv = e.target.result;
      const lines = csv.split('\n').filter((line: string) => line.trim() !== '');
      
      if (lines.length < 2) {
        this._errorMessage = 'El archivo CSV está vacío o no tiene el formato correcto.';
        return;
      }

      const fileHeaders = this.parseCSVLine(lines[0]);
      
      if (fileHeaders.length !== headers.length || !fileHeaders.every((header: string, index: number) => 
        header.trim().toLowerCase() === headers[index].toLowerCase())) {
        this._errorMessage = 'El archivo CSV no tiene el formato correcto. Por favor, descarga el archivo de ejemplo.';
        return;
      }
  
      const newTableData = lines.slice(1)
        .map((line: string) => {
          const values = this.parseCSVLine(line);
          return values.map((value: string) => value.trim());
        })
        .filter((row: string[]) => row.length === headers.length && row.some(value => value !== ''));

      if (newTableData.length === 0) {
        this._errorMessage = 'No hay datos válidos en el archivo';
        return;
      }

      if (newTableData.length > 1000) {
        this._errorMessage = 'El archivo no puede contener más de 1000 dispositivos';
        return;
      }

      this.validationErrors = [];
      const requiredFields = ['DeviceID'];
      
      newTableData.forEach((row, rowIndex) => {
        requiredFields.forEach(fieldName => {
          const fieldIndex = this.tableHeaders.findIndex(header => header.key === fieldName);
          if (fieldIndex !== -1) {
            const value = row[fieldIndex];
            if (!value || value.toString().trim() === '') {
              this.validationErrors.push({
                row: rowIndex,
                col: fieldIndex,
                type: 'required',
                message: `${fieldName} es requerido`
              });
            } else if (fieldName === 'DeviceID') {
              const deviceId = parseInt(value);
              if (isNaN(deviceId) || deviceId <= 0) {
                this.validationErrors.push({
                  row: rowIndex,
                  col: fieldIndex,
                  type: 'invalid',
                  message: `DeviceID debe ser un número válido mayor que 0`
                });
              }
            }
          }
        });
      });

      if (this.validationErrors.length > 0) {
        this.showValidationErrors = true;
        this._errorMessage = `Se encontraron ${this.validationErrors.length} errores de validación. Por favor, corrija los valores marcados en rojo.`;
        this.tableData = newTableData;
        this.currentStep = 2;
        this.updatePagination();
        return;
      }

      this.loadedDeviceIds = [...new Set(newTableData.map(row => parseInt(row[0])))];
      
      this.tableData = newTableData;
      this.currentStep = 2;
      this.updatePagination();
    };
  
    if (this.fileSelected) {
      reader.readAsText(this.fileSelected);
    }
  }

  parseCSVLine(line: string): string[] {
    const result = [];
    let current = '';
    let insideQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"' && (i === 0 || line[i - 1] !== '\\')) {
        insideQuotes = !insideQuotes;
      } else if (char === ',' && !insideQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  }

  updatePagination(): void {
    this.totalPages = Math.ceil(this.tableData.length / this.itemsPerPage);
    this.paginateTableData();
  }

  paginateTableData(): void {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    this.paginatedTableData = this.tableData.slice(start, end);
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.paginateTableData();
    }
  }
  
  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.paginateTableData();
    }
  }
  
  onPageChange(page: number) {
    if (page === -1) {
      this.currentPage = 1;
    } else if (page === -2) {
      this.currentPage = this.totalPages;
    } else if (page < 1 || page > this.totalPages) {
      return;
    } else {
      this.currentPage = page;
    }
    this.paginateTableData();
  }

  onCellEdit(rowIndex: number, colIndex: number, newValue: string): void {
    const globalRowIndex = (this.currentPage - 1) * this.itemsPerPage + rowIndex;
    this.tableData[globalRowIndex][colIndex] = newValue;
    this.paginatedTableData[rowIndex][colIndex] = newValue;
    
    this.clearValidationError(globalRowIndex, colIndex);
    this.validateField(globalRowIndex, colIndex, newValue);
    this.updateValidationState();
  }

  hasValidationError(rowIndex: number, colIndex: number): boolean {
    return this.validationErrors.some(error => error.row === rowIndex && error.col === colIndex);
  }

  getValidationError(rowIndex: number, colIndex: number): string {
    const error = this.validationErrors.find(error => error.row === rowIndex && error.col === colIndex);
    return error ? error.message : '';
  }

  clearValidationError(rowIndex: number, colIndex: number) {
    this.validationErrors = this.validationErrors.filter(error => 
      !(error.row === rowIndex && error.col === colIndex)
    );
    if (this.validationErrors.length === 0) {
      this.showValidationErrors = false;
    }
  }

  validateField(rowIndex: number, colIndex: number, value: string): void {
    const headerKey = this.tableHeaders[colIndex]?.key;
    
    if (headerKey === 'DeviceID') {
      const deviceId = parseInt(value);
      if (!value || value.toString().trim() === '') {
        this.validationErrors.push({
          row: rowIndex,
          col: colIndex,
          type: 'required',
          message: `DeviceID es requerido`
        });
      } else if (isNaN(deviceId) || deviceId <= 0) {
        this.validationErrors.push({
          row: rowIndex,
          col: colIndex,
          type: 'invalid',
          message: `DeviceID debe ser un número válido mayor que 0`
        });
      }
    }
  }

  updateValidationState(): void {
    this.showValidationErrors = this.validationErrors.length > 0;
    if (this.validationErrors.length > 0) {
      this._errorMessage = 'Por favor, complete los campos obligatorios';
    } else {
      this._errorMessage = '';
    }
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByCellIndex(index: number, item: any): number {
    return index;
  }

  getColumnWidth(index: number, header: any): any {
    if (index === 0) {
      return { 'width': '100px' };
    }
    return { 'width': 'auto' };
  }

  onConfirmLoad(): void {
    if (this.validationErrors.length > 0) {
      this._errorMessage = 'Por favor, corrija los errores de validación antes de continuar';
      return;
    }

    this.devicesLoaded.emit(this.loadedDeviceIds);
    this._toasterService.pop('success', null, `${this.loadedDeviceIds.length} dispositivos cargados correctamente`);
    this.onModalClose();
  }

  getLoadedDeviceIds(): number[] {
    return this.loadedDeviceIds;
  }
}

